import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import util from 'preact-util';
import { Text } from 'preact-i18n';

import { readCsvFile } from '../../lib/readCsvFile';

/**
 * Column name aliases for auto-mapping suggestions
 */
const COLUMN_ALIASES = {
    productNumber: [
        // Our own product CSV export header, listed first so a re-uploaded export maps on the
        // product number column and not on the internal 'id' column (TICKET-01714).
        'productnumber',
        'artikkelnr', 'varenr', 'sku', 'product number', 'produktnummer', 'vare nr', 'art.nr', 'artnr',
        'produktid', 'produkt id', 'product id', 'prod id', 'prod nr', 'prodnr',
        // Spelled-out + bare variants ('nr.'/'nr'/'nummer'/'id' last so specific aliases win the
        // substring pass; exact match e.g. the SolvIt "Id" column is caught regardless of order).
        'varenummer', 'artikkelnummer', 'produkt nr', 'nummer', 'nr.', 'nr', 'id'],
    gtin: ['ean-kode', 'ean', 'barcode', 'strekkode', 'gtin', 'ean kode'],
    // Name-type aliases first so a file with BOTH name and description columns (e.g. the SolvIt
    // export's "Name" + "Description") maps name to "Name", not to the HTML "Description".
    name: ['navn', 'name', 'produktnavn', 'varenavn', 'produkt', 'beskrivelse', 'description'],
    // 'inprice' / 'price' are our own export headers; the exact-match pass runs first, so the
    // 'price' substring alias cannot steal the 'inPrice' column.
    inPrice: ['listepris', 'innkjopspris', 'kostpris', 'cost', 'innpris', 'inn pris', 'innkjøpspris', 'innkj.pris', 'innkj', 'inprice'],
    price: ['veiledende utsalgspris', 'utsalgspris', 'salgspris', 'sales price', 'pris', 'retail', 'utsalg', 'veiledende utsalgspris inkl mva', 'pris inkl mva', 'price'],
};

const FIELD_LABELS = {
    productNumber: <Text id='product.csv-field-productnumber'>Product Number</Text>,
    gtin: <Text id='product.csv-field-gtin'>EAN/GTIN</Text>,
    name: <Text id='product.csv-field-name'>Name</Text>,
    inPrice: <Text id='product.csv-field-inprice'>Purchase Price (innpris)</Text>,
    price: <Text id='product.csv-field-price'>Sale Price (utsalgspris)</Text>,
};

const formatCurrency = (n) => new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currency: 'NOK',
    maximumFractionDigits: 2
}).format(n || 0);

const formatPercent = (n) => {
    if (n === null || n === undefined) return '-';
    const num = parseFloat(n);
    const prefix = num > 0 ? '+' : '';
    return `${prefix}${num.toFixed(1)}%`;
};

@observer
class CsvPriceUpload extends Component {
    constructor(props) {
        super(props);
        const preset = props.presetPreview;
        const initialSelected = {};
        if (preset && Array.isArray(preset.matchedProducts)) {
            preset.matchedProducts.forEach((p, idx) => { initialSelected[idx] = !p.suspicious; });
        }
        this.state = {
            step: preset ? 3 : 1, // 1: Upload, 2: Mapping, 3: Preview, 4: Result
            csvText: '',
            fileName: '',
            headers: [],
            columnMapping: {},
            detectedMapping: {},
            // VAT settings for price columns
            priceIncludesVat: true,
            inPriceIncludesVat: false,
            defaultVatRate: 25,
            matchedProducts: (preset && preset.matchedProducts) || [],
            unmatchedRows: (preset && preset.unmatchedRows) || [],
            isLoading: false,
            error: null,
            matchMode: 'identifier',
            createNewProducts: !!preset,
            selectedProducts: initialSelected,
            selectAll: true,
            updateResult: null,
        };
        this.fileInputRef = null;
    }

    handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            // Auto-detects UTF-8 vs Windows-1252; Excel on Norwegian Windows exports ANSI.
            const csvText = await readCsvFile(file);
            this.setState({
                csvText,
                fileName: file.name,
                error: null,
            }, () => {
                this.extractHeaders();
            });
        } catch (err) {
            this.setState({ error: 'Failed to read file' });
        }
    };

    handlePasteInput = (e) => {
        const csvText = e.target.value;
        this.setState({
            csvText,
            fileName: 'Pasted content',
            error: null,
        });
    };

    extractHeaders = () => {
        const { csvText } = this.state;
        if (!csvText) return;

        // Detect delimiter
        const firstLine = csvText.split('\n')[0];
        const counts = {
            ';': (firstLine.match(/;/g) || []).length,
            ',': (firstLine.match(/,/g) || []).length,
            '\t': (firstLine.match(/\t/g) || []).length,
        };
        const delimiter = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];

        // Parse headers
        let headers = firstLine.split(delimiter).map(h => h.trim().replace(/^\uFEFF/, ''));
        // Make duplicate headers unique by appending (2), (3), etc.
        const headerCounts = {};
        headers = headers.map(h => {
            if (!headerCounts[h]) {
                headerCounts[h] = 1;
                return h;
            }
            headerCounts[h] += 1;
            return `${h} (${headerCounts[h]})`;
        });

        // Auto-detect column mappings (two-pass: exact first, then substring)
        const detectedMapping = {};
        const normalizedHeaders = headers.map(h => h.toLowerCase().trim());
        const usedIndices = new Set();

        // Pass 1: exact matches only
        Object.entries(COLUMN_ALIASES).forEach(([field, aliases]) => {
            for (const alias of aliases) {
                const idx = normalizedHeaders.findIndex((h, i) => !usedIndices.has(i) && h === alias);
                if (idx !== -1) {
                    detectedMapping[field] = headers[idx];
                    usedIndices.add(idx);
                    break;
                }
            }
        });

        // Pass 2: substring matches for remaining unmapped fields
        Object.entries(COLUMN_ALIASES).forEach(([field, aliases]) => {
            if (detectedMapping[field]) return;
            for (const alias of aliases) {
                const idx = normalizedHeaders.findIndex((h, i) => !usedIndices.has(i) && h.includes(alias));
                if (idx !== -1) {
                    detectedMapping[field] = headers[idx];
                    usedIndices.add(idx);
                    break;
                }
            }
        });

        // Auto-detect VAT settings from column names
        let priceIncludesVat = true; // Default: assume sale price includes VAT
        let inPriceIncludesVat = false; // Default: assume purchase price excludes VAT

        if (detectedMapping.price) {
            const priceHeader = detectedMapping.price.toLowerCase();
            // Check the exclusion markers (eks/ekskl/excl) BEFORE the bare 'mva' fallback.
            // Otherwise a header like "Utsalgspris eks. mva" matches 'mva' first and is
            // wrongly treated as VAT-inclusive, dividing the price by 1.25 on import.
            if (priceHeader.includes('ekskl') || priceHeader.includes('excl') || priceHeader.includes('eks')) {
                priceIncludesVat = false;
            } else if (priceHeader.includes('inkl') || priceHeader.includes('incl') || priceHeader.includes('mva')) {
                priceIncludesVat = true;
            }
        }

        if (detectedMapping.inPrice) {
            const inPriceHeader = detectedMapping.inPrice.toLowerCase();
            if (inPriceHeader.includes('inkl') || inPriceHeader.includes('incl')) {
                inPriceIncludesVat = true;
            } else if (inPriceHeader.includes('ekskl') || inPriceHeader.includes('excl') || inPriceHeader.includes('eks')) {
                inPriceIncludesVat = false;
            }
            // "listepris" typically means supplier list price (excl VAT)
        }

        this.setState({
            headers,
            detectedMapping,
            columnMapping: { ...detectedMapping },
            priceIncludesVat,
            inPriceIncludesVat,
            step: 2,
        });
    };

    handleMappingChange = (field, value) => {
        this.setState(prevState => ({
            columnMapping: {
                ...prevState.columnMapping,
                // Keep '' for "Not mapped": an undefined key is dropped from the JSON body and
                // the backend then re-applies its own auto-detection for that field.
                [field]: value || '',
            },
        }));
    };

    submitPreview = async () => {
        const { csvText, columnMapping, matchMode, priceIncludesVat, inPriceIncludesVat, defaultVatRate } = this.state;
        const { productStore } = this.props.stores;

        this.setState({ isLoading: true, error: null });

        try {
            const vatSettings = {
                priceIncludesVat,
                inPriceIncludesVat,
                defaultVatRate,
            };
            const response = await productStore.uploadPriceCsvPreview(csvText, columnMapping, vatSettings, matchMode);

            if (response?.status === 200) {
                const { matchedProducts = [], unmatchedRows = [], detectedMapping = {} } = response.data || {};

                // Initialize products as selected (suspicious ones deselected)
                // Use row index as key to handle duplicate productIds
                const selectedProducts = {};
                matchedProducts.forEach((p, idx) => {
                    selectedProducts[idx] = !p.suspicious;
                });
                const allSelected = Object.values(selectedProducts).every(v => v);

                this.setState({
                    matchedProducts,
                    unmatchedRows,
                    detectedMapping: { ...detectedMapping },
                    selectedProducts,
                    selectAll: allSelected,
                    step: 3,
                    isLoading: false,
                });
            } else {
                this.setState({
                    error: response?.message || 'Failed to process CSV',
                    isLoading: false,
                });
            }
        } catch (err) {
            this.setState({
                error: err.message || 'An error occurred',
                isLoading: false,
            });
        }
    };

    toggleProductSelection = (idx) => {
        this.setState(prevState => {
            const newSelected = {
                ...prevState.selectedProducts,
                [idx]: !prevState.selectedProducts[idx],
            };
            const allSelected = Object.values(newSelected).every(v => v);
            return {
                selectedProducts: newSelected,
                selectAll: allSelected,
            };
        });
    };

    toggleSelectAll = () => {
        const { matchedProducts, selectAll } = this.state;
        const newSelectAll = !selectAll;
        const selectedProducts = {};
        matchedProducts.forEach((p, idx) => {
            selectedProducts[idx] = newSelectAll;
        });
        this.setState({
            selectedProducts,
            selectAll: newSelectAll,
        });
    };

    applyUpdates = async () => {
        const { matchedProducts, selectedProducts, createNewProducts, unmatchedRows } = this.state;
        const { productStore } = this.props.stores;

        // Build updates array from selected products (using index-based selection)
        const updates = matchedProducts
            .filter((p, idx) => selectedProducts[idx])
            .map(p => ({
                productId: p.productId,
                newPrice: p.newPrice,
                newInPrice: p.newInPrice,
                hasSegmentedPricing: p.hasSegmentedPricing || false,
            }));

        // Build new products array if creating new
        const newProducts = createNewProducts ? unmatchedRows.map(row => ({
            productNumber: row.productNumber,
            gtin: row.gtin,
            name: row.name,
            price: row.price,
            inPrice: row.inPrice,
        })) : [];

        this.setState({ isLoading: true, error: null });

        try {
            const { presetPreview } = this.props;
            const sourceList = presetPreview && presetPreview.list ? presetPreview.list.id : undefined;
            const sourceListName = presetPreview && presetPreview.list ? presetPreview.list.name : undefined;
            const response = await productStore.applyPriceUpdates(
                updates, createNewProducts, newProducts, { sourceList, sourceListName },
            );

            if (response?.status === 201) {
                this.setState({
                    updateResult: response.data,
                    step: 4,
                    isLoading: false,
                });
                // Reload products list
                if (this.props.onComplete) {
                    this.props.onComplete();
                }
            } else {
                this.setState({
                    error: response?.message || 'Failed to apply updates',
                    isLoading: false,
                });
            }
        } catch (err) {
            this.setState({
                error: err.message || 'An error occurred',
                isLoading: false,
            });
        }
    };

    goToStep = (step) => {
        this.setState({ step });
    };

    resetWizard = () => {
        this.setState({
            step: 1,
            csvText: '',
            fileName: '',
            headers: [],
            columnMapping: {},
            detectedMapping: {},
            matchMode: 'identifier',
            matchedProducts: [],
            unmatchedRows: [],
            isLoading: false,
            error: null,
            createNewProducts: false,
            selectedProducts: {},
            selectAll: true,
            updateResult: null,
        });
    };

    renderStep1Upload() {
        const { csvText, fileName, error } = this.state;
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        return (
            <div class='p-3'>
                <h5 class='mb-3'>
                    <i class='fa-solid fa-upload me-2' />
                    <Text id='product.csv-upload-step1'>Step 1: Upload Price List</Text>
                </h5>

                {error && (
                    <div class='alert alert-danger mb-3'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        {error}
                    </div>
                )}

                <div class='mb-3'>
                    <label class='form-label'>
                        <Text id='product.csv-select-file'>Select CSV file</Text>
                    </label>
                    <input
                        type='file'
                        class='form-control'
                        accept='.csv,.txt'
                        ref={ref => this.fileInputRef = ref}
                        onChange={this.handleFileSelect}
                    />
                    <small class='text-body-secondary'>
                        <Text id='product.csv-file-help'>Supports CSV files with semicolon, comma, or tab delimiters</Text>
                    </small>
                </div>

                <div class='text-center my-3'>
                    <span class='text-body-secondary'>- <Text id='product.csv-or'>or</Text> -</span>
                </div>

                <div class='mb-3'>
                    <label class='form-label'>
                        <Text id='product.csv-paste'>Paste CSV content</Text>
                    </label>
                    <textarea
                        class='form-control'
                        rows={6}
                        placeholder='Paste CSV content here...'
                        value={csvText}
                        onInput={this.handlePasteInput}
                    />
                </div>

                {fileName && (
                    <div class='alert alert-info mb-3'>
                        <i class='fa-solid fa-file-csv me-2' />
                        {fileName}
                    </div>
                )}

                <div class='d-flex justify-content-end gap-2'>
                    <button
                        type='button'
                        class='btn btn-outline-secondary'
                        onClick={this.props.onClose}
                    >
                        <Text id='input.cancel'>Cancel</Text>
                    </button>
                    <button
                        type='button'
                        class='btn btn-primary'
                        disabled={!csvText}
                        onClick={this.extractHeaders}
                    >
                        <Text id='product.csv-next'>Next</Text>
                        <i class='fa-solid fa-arrow-right ms-2' />
                    </button>
                </div>
            </div>
        );
    }

    renderStep2Mapping() {
        const {
            headers, columnMapping, detectedMapping, isLoading, error,
            priceIncludesVat, inPriceIncludesVat, defaultVatRate, matchMode,
        } = this.state;
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const hasProductIdentifier = matchMode === 'name'
            ? !!columnMapping.name
            : (columnMapping.productNumber || columnMapping.gtin);
        const hasPriceColumn = columnMapping.price || columnMapping.inPrice;

        return (
            <div class='p-3'>
                <h5 class='mb-3'>
                    <i class='fa-solid fa-columns me-2' />
                    <Text id='product.csv-upload-step2'>Step 2: Map Columns</Text>
                </h5>

                {error && (
                    <div class='alert alert-danger mb-3'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        {error}
                    </div>
                )}

                <p class='text-body-secondary mb-3'>
                    <Text id='product.csv-mapping-help'>
                        Map the CSV columns to product fields. Auto-detected mappings are pre-selected.
                    </Text>
                </p>

                {/* Match Mode Selector */}
                <div class='mb-3 p-3 rounded bg-body-secondary'>
                    <label class='form-label fw-bold mb-2'>
                        <i class='fa-solid fa-link me-2' />
                        <Text id='product.csv-match-mode'>Match Mode</Text>
                    </label>
                    <div class='form-check'>
                        <input
                            class='form-check-input'
                            type='radio'
                            name='matchMode'
                            id='matchModeIdentifier'
                            checked={matchMode === 'identifier'}
                            onChange={() => this.setState({ matchMode: 'identifier' })}
                        />
                        <label class='form-check-label' for='matchModeIdentifier'>
                            <Text id='product.csv-match-identifier'>Match by Product Number / EAN</Text>
                        </label>
                        <small class='d-block text-body-secondary'>
                            <Text id='product.csv-match-identifier-help'>
                                Requires Product Number or EAN/GTIN column mapped (one of them is enough)
                            </Text>
                        </small>
                    </div>
                    <div class='form-check mt-2'>
                        <input
                            class='form-check-input'
                            type='radio'
                            name='matchMode'
                            id='matchModeName'
                            checked={matchMode === 'name'}
                            onChange={() => this.setState({ matchMode: 'name' })}
                        />
                        <label class='form-check-label' for='matchModeName'>
                            <Text id='product.csv-match-name'>Match by Name (fuzzy)</Text>
                        </label>
                        <small class='d-block text-body-secondary'>
                            <Text id='product.csv-match-name-help'>
                                Matches products by name similarity. Requires Name column mapped.
                            </Text>
                        </small>
                    </div>
                </div>

                <div class='row g-3'>
                    {Object.entries(FIELD_LABELS).map(([field, label]) => {
                        const isDetected = detectedMapping[field];
                        // Identifier mode needs ONE of Product Number / EAN. Only star them while
                        // neither is mapped; a star on both read as "both required" (TICKET-01714).
                        const isRequired = matchMode === 'name'
                            ? field === 'name'
                            : ((field === 'productNumber' || field === 'gtin') && !hasProductIdentifier);

                        return (
                            <div class='col-md-6' key={field}>
                                <label class='form-label'>
                                    {label}
                                    {isRequired && <span class='text-danger'>*</span>}
                                    {isDetected && (
                                        <span class='badge bg-success ms-2'>
                                            <i class='fa-solid fa-wand-magic-sparkles me-1' />
                                            <Text id='product.csv-auto-detected'>Auto-detected</Text>
                                        </span>
                                    )}
                                </label>
                                <select
                                    class='form-select'
                                    value={columnMapping[field] || ''}
                                    onChange={(e) => this.handleMappingChange(field, e.target.value)}
                                >
                                    <option value=''>-- <Text id='product.csv-not-mapped'>Not mapped</Text> --</option>
                                    {headers.map(header => (
                                        <option key={header} value={header}>{header}</option>
                                    ))}
                                </select>
                            </div>
                        );
                    })}
                </div>

                {!hasProductIdentifier && (
                    <div class='alert alert-warning mt-3'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        {matchMode === 'name'
                            ? <Text id='product.csv-need-name'>You must map the Name column for name matching.</Text>
                            : <Text id='product.csv-need-identifier'>
                                You must map at least Product Number or EAN/GTIN to match products.
                            </Text>
                        }
                    </div>
                )}

                {!hasPriceColumn && (
                    <div class='alert alert-warning mt-3'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        <Text id='product.csv-need-price'>
                            You must map at least one price column (Purchase Price or Sale Price).
                        </Text>
                    </div>
                )}

                {/* VAT Settings */}
                {hasPriceColumn && (
                    <div class={`mt-4 p-3 rounded ${darkmode ? 'bg-dark' : 'bg-light'}`}>
                        <h6 class='mb-3'>
                            <i class='fa-solid fa-percent me-2' />
                            <Text id='product.csv-vat-settings'>VAT Settings</Text>
                        </h6>

                        <div class='row g-3'>
                            {columnMapping.price && (
                                <div class='col-md-6'>
                                    <div class='form-check form-switch'>
                                        <input
                                            class='form-check-input'
                                            type='checkbox'
                                            id='priceIncludesVat'
                                            checked={priceIncludesVat}
                                            onChange={(e) => this.setState({ priceIncludesVat: e.target.checked })}
                                        />
                                        <label class='form-check-label' for='priceIncludesVat'>
                                            <Text id='product.csv-price-incl-vat'>Sale Price includes VAT (inkl. mva)</Text>
                                        </label>
                                    </div>
                                    <small class='text-body-secondary'>
                                        {priceIncludesVat
                                            ? <Text id='product.csv-price-vat-removed'>VAT will be removed before storing (Pris = price excl. VAT)</Text>
                                            : <Text id='product.csv-price-vat-asis'>Price will be stored as-is (excl. VAT)</Text>
                                        }
                                    </small>
                                </div>
                            )}

                            {columnMapping.inPrice && (
                                <div class='col-md-6'>
                                    <div class='form-check form-switch'>
                                        <input
                                            class='form-check-input'
                                            type='checkbox'
                                            id='inPriceIncludesVat'
                                            checked={inPriceIncludesVat}
                                            onChange={(e) => this.setState({ inPriceIncludesVat: e.target.checked })}
                                        />
                                        <label class='form-check-label' for='inPriceIncludesVat'>
                                            <Text id='product.csv-inprice-incl-vat'>Purchase Price includes VAT (inkl. mva)</Text>
                                        </label>
                                    </div>
                                    <small class='text-body-secondary'>
                                        {inPriceIncludesVat
                                            ? <Text id='product.csv-inprice-vat-removed'>VAT will be removed before storing (InPrice = price excl. VAT)</Text>
                                            : <Text id='product.csv-price-vat-asis'>Price will be stored as-is (excl. VAT)</Text>
                                        }
                                    </small>
                                </div>
                            )}

                            <div class='col-md-6'>
                                <label class='form-label'>
                                    <Text id='product.csv-default-vat'>Default VAT Rate (%)</Text>
                                </label>
                                <select
                                    class='form-select'
                                    value={defaultVatRate}
                                    onChange={(e) => this.setState({ defaultVatRate: parseInt(e.target.value, 10) })}
                                >
                                    <option value='25'>25% (<Text id='product.csv-vat-standard'>Standard</Text>)</option>
                                    <option value='15'>15% (<Text id='product.csv-vat-food'>Food</Text>)</option>
                                    <option value='12'>12% (<Text id='product.csv-vat-transport'>Transport</Text>)</option>
                                    <option value='0'>0% (<Text id='product.csv-vat-exempt'>Exempt</Text>)</option>
                                </select>
                                <small class='text-body-secondary'>
                                    <Text id='product.csv-vat-help'>Used for VAT calculations. Product's own VAT rate is used if available.</Text>
                                </small>
                            </div>
                        </div>
                    </div>
                )}

                <div class='d-flex justify-content-between gap-2 mt-4'>
                    <button
                        type='button'
                        class='btn btn-outline-secondary'
                        onClick={() => this.goToStep(1)}
                    >
                        <i class='fa-solid fa-arrow-left me-2' />
                        <Text id='product.csv-back'>Back</Text>
                    </button>
                    <button
                        type='button'
                        class='btn btn-primary'
                        disabled={!hasProductIdentifier || !hasPriceColumn || isLoading}
                        onClick={this.submitPreview}
                    >
                        {isLoading ? (
                            <>
                                <span class='spinner-border spinner-border-sm me-2' />
                                <Text id='product.csv-processing'>Processing...</Text>
                            </>
                        ) : (
                            <>
                                <Text id='product.csv-preview'>Preview Changes</Text>
                                <i class='fa-solid fa-arrow-right ms-2' />
                            </>
                        )}
                    </button>
                </div>
            </div>
        );
    }

    renderStep3Preview() {
        const { matchedProducts, unmatchedRows, selectedProducts, selectAll, createNewProducts, isLoading, error } = this.state;
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const selectedCount = Object.values(selectedProducts).filter(v => v).length;

        return (
            <div class='p-3'>
                <h5 class='mb-3'>
                    <i class='fa-solid fa-eye me-2' />
                    <Text id='product.csv-upload-step3'>Step 3: Preview Changes</Text>
                </h5>

                {error && (
                    <div class='alert alert-danger mb-3'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        {error}
                    </div>
                )}

                <div class='d-flex gap-3 mb-3'>
                    <div class='badge bg-success p-2'>
                        <i class='fa-solid fa-check-circle me-1' />
                        {matchedProducts.length} <Text id='product.csv-matched'>matched</Text>
                    </div>
                    <div class='badge bg-warning text-dark p-2'>
                        <i class='fa-solid fa-question-circle me-1' />
                        {unmatchedRows.length} <Text id='product.csv-unmatched'>unmatched</Text>
                    </div>
                    <div class='badge bg-primary p-2'>
                        <i class='fa-solid fa-check-square me-1' />
                        {selectedCount} <Text id='product.csv-selected'>selected for update</Text>
                    </div>
                </div>

                {/* Suspicious matches warning */}
                {matchedProducts.filter(p => p.suspicious).length > 0 && (
                    <div class='alert alert-danger mb-3'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        <strong>
                            <Text id='product.csv-suspicious-count' fields={{ count: matchedProducts.filter(p => p.suspicious).length }}>
                                {matchedProducts.filter(p => p.suspicious).length} suspicious matches found!
                            </Text>
                        </strong>
                        {' '}
                        <Text id='product.csv-suspicious-help'>
                            These have a new price of 0, extreme price differences or mismatched product names (e.g. pack vs single unit).
                            They are deselected by default. Review the CSV Name vs local Name before selecting.
                        </Text>
                    </div>
                )}

                {/* Matched Products Table */}
                {matchedProducts.length > 0 && (
                    <div class='mb-4'>
                        <h6>
                            <i class='fa-solid fa-check text-success me-2' />
                            <Text id='product.csv-matched-products'>Matched Products</Text>
                        </h6>
                        <div class='table-responsive' style='max-height: 300px; overflow-y: auto;'>
                            <table class='table table-sm table-hover mb-0'>
                                <thead class={`sticky-top ${darkmode ? 'bg-dark' : 'bg-light'}`}>
                                    <tr>
                                        <th style='width: 40px;'>
                                            <input
                                                type='checkbox'
                                                class='form-check-input'
                                                checked={selectAll}
                                                onChange={this.toggleSelectAll}
                                            />
                                        </th>
                                        <th><Text id='product.csv-col-prodnum'>Prod #</Text></th>
                                        <th>Match</th>
                                        <th><Text id='product.csv-col-name'>Name</Text></th>
                                        <th>CSV Name</th>
                                        <th class='text-end'><Text id='product.csv-col-current-price'>Current Price</Text></th>
                                        <th class='text-end'><Text id='product.csv-col-new-price'>New Price</Text></th>
                                        <th class='text-end'><Text id='product.csv-col-diff'>Diff</Text></th>
                                        <th class='text-end'><Text id='product.csv-col-current-inprice'>Current InPrice</Text></th>
                                        <th class='text-end'><Text id='product.csv-col-new-inprice'>New InPrice</Text></th>
                                        <th class='text-end'><Text id='product.csv-col-diff'>Diff</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {matchedProducts.map((product, idx) => {
                                        const priceDiffClass = product.priceDiff > 0 ? 'text-success' : product.priceDiff < 0 ? 'text-danger' : '';
                                        const inPriceDiffClass = product.inPriceDiff > 0 ? 'text-success' : product.inPriceDiff < 0 ? 'text-danger' : '';
                                        const rowClass = product.suspicious
                                            ? 'table-danger'
                                            : (!selectedProducts[idx] ? 'text-muted' : '');

                                        return (
                                            <tr key={`${product.productId}-${idx}`} class={rowClass}>
                                                <td>
                                                    <input
                                                        type='checkbox'
                                                        class='form-check-input'
                                                        checked={selectedProducts[idx] || false}
                                                        onChange={() => this.toggleProductSelection(idx)}
                                                    />
                                                </td>
                                                <td>
                                                    <small>{product.productNumber || product.gtin || '-'}</small>
                                                </td>
                                                <td>
                                                    {product.matchType === 'productNumber' && (
                                                        <span class='badge bg-success' title={`Matched by product number: ${product.matchValue}`}>Prod#</span>
                                                    )}
                                                    {product.matchType === 'gtin' && (
                                                        <span class='badge bg-info text-dark' title={`Matched by EAN: ${product.matchValue}`}>EAN</span>
                                                    )}
                                                    {product.matchType === 'name' && (
                                                        <span class='badge bg-warning text-dark' title={`Matched by name: "${product.matchValue}"`}>
                                                            Name {product.matchScore}%
                                                        </span>
                                                    )}
                                                    {product.suspicious && (
                                                        <span class='badge bg-danger ms-1' title={product.suspiciousReasons?.join('; ')}>
                                                            <i class='fa-solid fa-triangle-exclamation' />
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <small class='text-truncate d-block' style='max-width: 160px;' title={product.name}>
                                                        {product.name}
                                                    </small>
                                                </td>
                                                <td>
                                                    <small class='text-truncate d-block' style='max-width: 160px;' title={product.csvName}>
                                                        {product.csvName || '-'}
                                                    </small>
                                                </td>
                                                <td class='text-end'>
                                                    <small>
                                                        {formatCurrency(product.currentPrice)}
                                                        {product.hasSegmentedPricing && (
                                                            <span class='badge bg-info text-dark ms-1' title='Segmented price (medicine + fee + equipment + other). Segments will be cleared on update.'>
                                                                seg
                                                            </span>
                                                        )}
                                                    </small>
                                                </td>
                                                <td class='text-end'>
                                                    {product.newPrice !== undefined ? (
                                                        <small class='fw-bold'>
                                                            {formatCurrency(product.newPrice)}
                                                            {product.priceVatAdjusted && (
                                                                <span class='badge bg-warning text-dark ms-1' title={`VAT ${product.vatRate}% removed from CSV price`}>
                                                                    -mva
                                                                </span>
                                                            )}
                                                        </small>
                                                    ) : (
                                                        <small class='text-muted'>-</small>
                                                    )}
                                                </td>
                                                <td class={`text-end ${priceDiffClass}`}>
                                                    {product.priceDiffPercent !== undefined && product.priceDiffPercent !== null ? (
                                                        <small>{formatPercent(product.priceDiffPercent)}</small>
                                                    ) : (
                                                        <small>-</small>
                                                    )}
                                                </td>
                                                <td class='text-end'>
                                                    <small>{formatCurrency(product.currentInPrice)}</small>
                                                </td>
                                                <td class='text-end'>
                                                    {product.newInPrice !== undefined ? (
                                                        <small class='fw-bold'>
                                                            {formatCurrency(product.newInPrice)}
                                                            {product.inPriceVatAdjusted && (
                                                                <span class='badge bg-warning text-dark ms-1' title={`VAT ${product.vatRate}% removed`}>
                                                                    -mva
                                                                </span>
                                                            )}
                                                        </small>
                                                    ) : (
                                                        <small class='text-muted'>-</small>
                                                    )}
                                                </td>
                                                <td class={`text-end ${inPriceDiffClass}`}>
                                                    {product.inPriceDiffPercent !== undefined && product.inPriceDiffPercent !== null ? (
                                                        <small>{formatPercent(product.inPriceDiffPercent)}</small>
                                                    ) : (
                                                        <small>-</small>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Unmatched Rows */}
                {unmatchedRows.length > 0 && (
                    <div class='mb-4'>
                        <h6>
                            <i class='fa-solid fa-question-circle text-warning me-2' />
                            <Text id='product.csv-unmatched-rows'>Unmatched Rows</Text>
                        </h6>
                        <div class='form-check mb-2'>
                            <input
                                type='checkbox'
                                class='form-check-input'
                                id='createNewProducts'
                                checked={createNewProducts}
                                onChange={(e) => this.setState({ createNewProducts: e.target.checked })}
                            />
                            <label class='form-check-label' for='createNewProducts'>
                                <Text id='product.csv-create-new'>Create new products for unmatched rows</Text>
                            </label>
                        </div>
                        <div class='table-responsive' style='max-height: 200px; overflow-y: auto;'>
                            <table class='table table-sm table-hover mb-0'>
                                <thead class={`sticky-top ${darkmode ? 'bg-dark' : 'bg-light'}`}>
                                    <tr>
                                        <th><Text id='product.csv-col-row'>Row</Text></th>
                                        <th><Text id='product.csv-col-prodnum'>Prod #</Text></th>
                                        <th><Text id='product.csv-col-ean'>EAN</Text></th>
                                        <th><Text id='product.csv-col-name'>Name</Text></th>
                                        <th class='text-end'><Text id='product.csv-col-price'>Price</Text></th>
                                        <th class='text-end'><Text id='product.csv-col-inprice'>InPrice</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unmatchedRows.slice(0, 50).map(row => (
                                        <tr key={row.rowIndex} class={createNewProducts ? '' : 'text-muted'}>
                                            <td><small>{row.rowIndex}</small></td>
                                            <td><small>{row.productNumber || '-'}</small></td>
                                            <td><small>{row.gtin || '-'}</small></td>
                                            <td>
                                                <small class='text-truncate d-block' style='max-width: 150px;' title={row.name}>
                                                    {row.name || '-'}
                                                </small>
                                            </td>
                                            <td class='text-end'>
                                                <small>
                                                    {typeof row.price === 'number' ? row.price.toFixed(2) : (row.price || '-')}
                                                    {row.priceVatAdjusted && (
                                                        <span class='badge bg-warning text-dark ms-1' title='VAT removed'>-mva</span>
                                                    )}
                                                </small>
                                            </td>
                                            <td class='text-end'>
                                                <small>
                                                    {typeof row.inPrice === 'number' ? row.inPrice.toFixed(2) : (row.inPrice || '-')}
                                                    {row.inPriceVatAdjusted && (
                                                        <span class='badge bg-warning text-dark ms-1' title='VAT removed'>-mva</span>
                                                    )}
                                                </small>
                                            </td>
                                        </tr>
                                    ))}
                                    {unmatchedRows.length > 50 && (
                                        <tr>
                                            <td colspan='6' class='text-center text-muted'>
                                                <small>... and {unmatchedRows.length - 50} more rows</small>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <div class='d-flex justify-content-between gap-2 mt-4'>
                    <button
                        type='button'
                        class='btn btn-outline-secondary'
                        onClick={() => this.goToStep(2)}
                    >
                        <i class='fa-solid fa-arrow-left me-2' />
                        <Text id='product.csv-back'>Back</Text>
                    </button>
                    <button
                        type='button'
                        class='btn btn-success'
                        disabled={selectedCount === 0 && !createNewProducts || isLoading}
                        onClick={this.applyUpdates}
                    >
                        {isLoading ? (
                            <>
                                <span class='spinner-border spinner-border-sm me-2' />
                                <Text id='product.csv-applying'>Applying...</Text>
                            </>
                        ) : (
                            <>
                                <i class='fa-solid fa-check me-2' />
                                <Text id='product.csv-apply'>Apply {selectedCount} Updates</Text>
                                {createNewProducts && unmatchedRows.length > 0 && (
                                    <> + {unmatchedRows.length} New</>
                                )}
                            </>
                        )}
                    </button>
                </div>
            </div>
        );
    }

    renderStep4Result() {
        const { updateResult } = this.state;

        return (
            <div class='p-3 text-center'>
                <div class='mb-4'>
                    <i class='fa-solid fa-check-circle text-success' style='font-size: 4rem;' />
                </div>
                <h5 class='mb-3'>
                    <Text id='product.csv-upload-complete'>Price Update Complete</Text>
                </h5>
                <p class='text-body-secondary mb-4'>
                    {updateResult?.updatedCount > 0 && (
                        <span class='badge bg-success me-2'>
                            {updateResult.updatedCount} <Text id='product.csv-products-updated'>products updated</Text>
                        </span>
                    )}
                    {updateResult?.insertedCount > 0 && (
                        <span class='badge bg-primary'>
                            {updateResult.insertedCount} <Text id='product.csv-products-created'>new products created</Text>
                        </span>
                    )}
                </p>
                <div class='d-flex justify-content-center gap-2'>
                    <button
                        type='button'
                        class='btn btn-outline-secondary'
                        onClick={this.resetWizard}
                    >
                        <i class='fa-solid fa-redo me-2' />
                        <Text id='product.csv-upload-another'>Upload Another</Text>
                    </button>
                    <button
                        type='button'
                        class='btn btn-primary'
                        onClick={this.props.onClose}
                    >
                        <Text id='product.csv-done'>Done</Text>
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const { step } = this.state;
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        return (
            <div class={`card ${darkmode ? 'bg-dark text-white' : ''}`}>
                <div class={`card-header d-flex justify-content-between align-items-center ${darkmode ? 'bg-dark' : ''}`}>
                    <span>
                        <i class='fa-solid fa-file-csv me-2' />
                        <Text id='product.csv-upload-title'>Upload Price List</Text>
                    </span>
                    <button
                        type='button'
                        class={`btn-close ${darkmode ? 'btn-close-white' : ''}`}
                        onClick={this.props.onClose}
                    />
                </div>

                {/* Progress indicator */}
                <div class={`px-3 pt-3 ${darkmode ? 'bg-dark' : 'bg-body-tertiary'}`}>
                    <div class='d-flex justify-content-between mb-2'>
                        {[1, 2, 3, 4].map(s => (
                            <div
                                key={s}
                                class={`d-flex align-items-center ${s < 4 ? 'flex-grow-1' : ''}`}
                            >
                                <div
                                    class={`rounded-circle d-flex align-items-center justify-content-center ${
                                        step >= s ? 'bg-primary text-white' : 'bg-body-secondary text-body-secondary'
                                    }`}
                                    style='width: 28px; height: 28px; font-size: 0.8rem;'
                                >
                                    {step > s ? <i class='fa-solid fa-check' /> : s}
                                </div>
                                {s < 4 && (
                                    <div
                                        class={`flex-grow-1 mx-2 ${step > s ? 'bg-primary' : 'bg-body-secondary'}`}
                                        style='height: 2px;'
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                    <div class='d-flex justify-content-between text-body-secondary' style='font-size: 0.75rem;'>
                        <span><Text id='product.csv-step-upload'>Upload</Text></span>
                        <span><Text id='product.csv-step-map'>Map</Text></span>
                        <span><Text id='product.csv-step-preview'>Preview</Text></span>
                        <span><Text id='product.csv-step-done'>Done</Text></span>
                    </div>
                </div>

                <div class='card-body'>
                    {step === 1 && this.renderStep1Upload()}
                    {step === 2 && this.renderStep2Mapping()}
                    {step === 3 && this.renderStep3Preview()}
                    {step === 4 && this.renderStep4Result()}
                </div>
            </div>
        );
    }
}

export default CsvPriceUpload;
